import type React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Container,
  Tabs,
  Tab,
  Paper,
  Stack,
  useTheme,
  IconButton,
  useMediaQuery,
  Typography,
  Grid,
  Button,
  SxProps,
  Theme,
  CircularProgress,
} from "@mui/material";
import { Share } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { tokens } from "../../../../theme";
import type { DeceasedPerson } from "../../../bookings/types/edit-booking";
import type { RootState } from "../../../../store/Index";
import apiService, { BaseURL } from "../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../components/common/ShowToast";
import type {
  IStreamDetails,
  IStreamResponse,
} from "./components/types/memorial-types";
import { useMemorialTheme } from "./components/hooks/useTheme";
import ThemedHeader from "./components/ThemedHeader";
import ShareControls from "./components/ShareControls";
import GoFundMeWidget from "../../../../components/GoFundMeWidget";
import ShareDrawer from "./components/ShareDrawer";
import ObituaryTab from "./components/tabs/OrbituaryTab";
import MemoriesTab from "./components/tabs/MemoriesTab";
import DonationsTab from "./components/tabs/DonationTab";
import { DownloadsTab } from "./components/tabs/DownloadsTab";
import CommentCard from "./components/CommentCard";
import StreamViewer from "./components/StreamViewer";
import EventHeader from "./components/EventHeader";
import ServiceDetails from "./components/ServiceDetails"; // <-- Added import
import streamLinks from "./old-stream-links.json";
import AdminTab from "./components/tabs/admin-tab/AdminTab";
import ConferenceSidePanel from "./components/ConferenceSidePanel";

interface IMessage {
  id: number;
  sender_full_name: string | null;
  sender_email: string;
  content: string;
  created_at: string;
  is_approved: boolean;
  parent: number | null;
}

interface IApiMessageResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: IMessage[];
}

interface ICommentType {
  id: string;
  author: string;
  date: string;
  content: string;
  is_approved?: boolean;
  replies?: ICommentType[];
  parent_message?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  sx?: SxProps<Theme>;
}

type EnrichedStream = {
  index: number;
  start: Date | null;
  end: Date | null;
};

type ValidStream = {
  index: number;
  start: Date;
  end: Date;
};

interface StreamProgramResponse {
  id: number;
  file?: string;
  created_at: string;
  updated_at: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

const isValidStream = (s: EnrichedStream): s is ValidStream =>
  s.start instanceof Date && s.end instanceof Date;

const URL_TO_BOOKING_ID: Readonly<Record<string, string>> = Object.freeze(
  streamLinks.reduce(
    (acc, { booking_id, website_stream_link }) => {
      acc[website_stream_link] = String(booking_id);
      return acc;
    },
    {} as Record<string, string>,
  ),
);

function TabPanel({ children, value, index, sx, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`memorial-tabpanel-${index}`}
      aria-labelledby={`memorial-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            width: "100%",
            p: 0,
            m: 0,
            ...(sx || {}),
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

export default function MemorialPage() {
  const [tabValue, setTabValue] = useState(1);
  const [activeStreamIndex, setActiveStreamIndex] = useState(0);
  const [shareDrawerOpen, setShareDrawerOpen] = useState(false);
  const [deceasedData, setDeceasedData] = useState<DeceasedPerson | null>(null);
  const [streams, setStreams] = useState<IStreamDetails[]>([]);
  const [messages, setMessages] = useState<ICommentType[]>([]);
  const [isLoadingDeceased, setIsLoadingDeceased] = useState(false);
  const [isLoadingStreams, setIsLoadingStreams] = useState(false);
  const [streamProgram, setStreamProgram] = useState<string | null>(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { id: bookingIdParam } = useParams<{ id: string }>();
  const [bookingId, setBookingId] = useState<string | undefined>(undefined);
  const socketRef = useRef<WebSocket | null>(null);
  const [downloadingProgram, setDownloadingProgram] = useState(false);
  const auth = useSelector((state: RootState) => state.auth);
  const isAdmin =
    auth.user?.permissions.includes("chat.delete_memorialmessage") || false;

  const memorialTheme = useMemorialTheme(streams);
  const currentStream = streams[activeStreamIndex];

  const fetchStreamDetails = useCallback(async () => {
    if (!bookingId) return;
    setIsLoadingStreams(true);
    try {
      const response = await apiService.get<IStreamResponse>(
        `/api/streams/stream/?booking=${bookingId}`,
      );

      setStreams(response.data.results);

      if (response.data.results.length === 1) {
        setActiveStreamIndex(0);
      }
    } catch (error) {
      console.error("Failed to fetch stream details:", error);
      showErrorToast("Failed to load stream details.", "stream-details-error");
      setStreams([]);
    } finally {
      setIsLoadingStreams(false);
    }
  }, [bookingId]);

  const fetchDeceasedDetails = useCallback(async (deceasedId: number) => {
    setIsLoadingDeceased(true);
    try {
      const response = await apiService.get<DeceasedPerson>(
        `/api/individuals/deceased/${deceasedId}/retrieve-by-id/`,
        {},
        true,
      );

      setDeceasedData(response.data);
    } catch (error) {
      console.error("Failed to fetch deceased individual details:", error);
      showErrorToast(
        "Failed to load obituary details.",
        "obituary-details-error",
      );
      setDeceasedData(null);
    } finally {
      setIsLoadingDeceased(false);
    }
  }, []);

  useEffect(() => {
    fetchStreamDetails();
  }, [fetchStreamDetails]);

  useEffect(() => {
    if (currentStream?.booking?.deceased?.[0]?.id) {
      fetchDeceasedDetails(currentStream.booking.deceased[0].id);
    }
  }, [currentStream, fetchDeceasedDetails]);

  useEffect(() => {
    const currentUrl = window.location.href;

    const mappedId = URL_TO_BOOKING_ID[currentUrl];

    if (mappedId) {
      setBookingId(mappedId);
    } else {
      setBookingId(bookingIdParam);
    }
  }, [bookingIdParam]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleStreamChange = (streamIndex: number) => {
    setActiveStreamIndex(streamIndex);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  const buildMessageTree = useCallback(
    (msgs: ICommentType[]): ICommentType[] => {
      const map: { [key: string]: ICommentType } = {};
      const roots: ICommentType[] = [];

      msgs.forEach((msg) => {
        map[msg.id] = { ...msg, replies: [] };
      });

      msgs.forEach((msg) => {
        if (msg.parent_message && map[msg.parent_message]) {
          map[msg.parent_message].replies!.push(map[msg.id]);
        } else {
          roots.push(map[msg.id]);
        }
      });

      const sortMessages = (messages: ICommentType[]) => {
        messages.sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        );
        messages.forEach((m) => {
          if (m.replies) sortMessages(m.replies);
        });
      };

      sortMessages(roots);
      return roots;
    },
    [],
  );

  const getServiceStart = useCallback(
    (stream: { date?: string | null; start?: string | null }): Date | null => {
      const date = stream.date;
      const start = stream.start;

      if (!date || !start) {
        console.warn("Missing start fields", stream);
        return null;
      }

      const d = new Date(`${date}T${start}+02:00`);

      if (isNaN(d.getTime())) {
        console.warn("Invalid START date", { date, start });
        return null;
      }

      return d;
    },
    [],
  );

  const getServiceEnd = useCallback(
    (stream: { date?: string | null; end?: string | null }): Date | null => {
      const date = stream.date;
      const end = stream.end;

      if (!date || !end) {
        console.warn("Missing end fields", stream);
        return null;
      }

      const d = new Date(`${date}T${end}+02:00`);

      if (isNaN(d.getTime())) {
        console.warn("Invalid END date", { date, end });
        return null;
      }

      return d;
    },
    [],
  );

  const getDefaultActiveStreamIndex = useCallback(
    (
      streams: Array<{
        date?: string | null;
        start?: string | null;
        end?: string | null;
      }>,
    ) => {
      if (!streams.length) return 0;

      const now = new Date();

      const enriched: EnrichedStream[] = streams.map((stream, index) => ({
        index,
        start: getServiceStart(stream),
        end: getServiceEnd(stream),
      }));

      const valid = enriched.filter(isValidStream);

      if (!valid.length) return 0;

      const ongoing = valid.find((s) => now >= s.start && now <= s.end);

      if (ongoing) {
        return ongoing.index;
      }

      const upcoming = valid
        .filter((s) => s.start > now)
        .sort((a, b) => a.start.getTime() - b.start.getTime());

      if (upcoming.length) {
        return upcoming[0].index;
      }

      const past = valid
        .filter((s) => s.end < now)
        .sort((a, b) => b.end.getTime() - a.end.getTime());

      return past[0]?.index ?? 0;
    },
    [getServiceEnd, getServiceStart],
  );

  const reduxTheme = useSelector((state: RootState) => state.theme);
  useEffect(() => {
    if (streams.length) {
      const defaultIndex = getDefaultActiveStreamIndex(streams);
      setActiveStreamIndex(defaultIndex);
    }
  }, [getDefaultActiveStreamIndex, streams]);

  const flattenMessages = useCallback(
    (msgs: ICommentType[]): ICommentType[] => {
      const flatten = (messages: ICommentType[]): ICommentType[] => {
        let flat: ICommentType[] = [];
        messages.forEach((m) => {
          flat.push({ ...m, replies: undefined });
          if (m.replies) {
            flat = flat.concat(flatten(m.replies));
          }
        });
        return flat;
      };
      return flatten(msgs);
    },
    [],
  );

  const fetchMessages = useCallback(async () => {
    if (!currentStream?.id) return;
    try {
      let query = `stream_id=${currentStream.id}`;
      if (!isAdmin) {
        query += "&is_approved=true";
      }
      const response = await apiService.get<IApiMessageResponse>(
        `/api/chats/memorial-message/?${query}`,
        undefined,
        true,
      );
      const fetched = response.data.results.map((msg: IMessage) => ({
        id: msg.id.toString(),
        author: msg.sender_full_name || "Anonymous",
        date: new Date(msg.created_at).toLocaleString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }),
        content: msg.content,
        is_approved: msg.is_approved,
        parent_message: msg.parent ? msg.parent.toString() : undefined,
      }));
      setMessages(buildMessageTree(fetched));
    } catch (error) {
      console.error("Failed to fetch messages:", error);
      showErrorToast("Failed to load messages.", "messages-fetch-error");
    }
  }, [buildMessageTree, currentStream?.id, isAdmin]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const removeById = useCallback(
    (msgs: ICommentType[], id: string): ICommentType[] => {
      return msgs
        .filter((m) => m.id !== id)
        .map((m) => ({
          ...m,
          replies: m.replies ? removeById(m.replies, id) : [],
        }));
    },
    [],
  );

  useEffect(() => {
    if (currentStream?.id) {
      const wsProtocol = BaseURL.startsWith("https") ? "wss" : "ws";
      const wsHost = new URL(BaseURL).host;
      const wsUrl = `${wsProtocol}://${wsHost}/ws/stream-memorial/${currentStream.id}/`;

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log("Connected to WebSocket");
        ws.send(JSON.stringify({ type: "refetch" }));
      };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.error) {
            showErrorToast(data.error, "websocket-error");
            return;
          }
          if (data.type === "delete") {
            setMessages((prev) => removeById(prev, data.id));
          } else if (data.type === "all_messages") {
            const fetchedMessages = (
              data.messages as Array<{
                id: number | string;
                sender_full_name?: string | null;
                timestamp?: string;
                message?: string;
                is_approved?: boolean;
                parent_message?: number | string | null;
              }>
            ).map((msg) => ({
              id: msg.id.toString(),
              author: msg.sender_full_name || "Anonymous",
              date: new Date(msg.timestamp || Date.now()).toLocaleString(
                "en-US",
                {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                },
              ),
              content: msg.message || "",
              is_approved: msg.is_approved,
              parent_message: msg.parent_message
                ? msg.parent_message.toString()
                : undefined,
            }));
            setMessages(buildMessageTree(fetchedMessages));
          } else {
            const newMsg: ICommentType = {
              id: data.id.toString(),
              author: data.sender_full_name || "Anonymous",
              date: new Date(data.timestamp || Date.now()).toLocaleString(
                "en-US",
                {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                },
              ),
              content: data.message || "",
              is_approved: data.is_approved,
              parent_message: data.parent_message
                ? data.parent_message.toString()
                : undefined,
            };

            setMessages((prev) => {
              const flatPrev = flattenMessages(prev);
              const existsIndex = flatPrev.findIndex((m) => m.id === newMsg.id);
              let updatedFlat;
              if (existsIndex !== -1) {
                updatedFlat = [...flatPrev];
                updatedFlat[existsIndex] = newMsg;
              } else {
                updatedFlat = [...flatPrev, newMsg];
              }
              return buildMessageTree(updatedFlat);
            });
          }
        } catch (e) {
          console.error("Failed to parse WebSocket message:", e, event.data);
        }
      };
      ws.onerror = (err) => console.error("WebSocket error:", err);
      ws.onclose = () => console.log("WebSocket disconnected");

      socketRef.current = ws;

      return () => {
        ws.close();
      };
    }
  }, [
    buildMessageTree,
    currentStream?.id,
    flattenMessages,
    isAdmin,
    removeById,
  ]);

  const handleApprove = (id: string, approve: boolean) => {
    if (!isAdmin) {
      showErrorToast(
        "Only admins can approve messages.",
        "approve-permission-error",
      );
      return;
    }
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({ type: "approve", id, approve }));
      showSuccessToast(
        `Message ${approve ? "approved" : "disapproved"} successfully!`,
      );
    } else {
      console.error("WebSocket is not open. Approval not sent.");
      showErrorToast("Failed to update approval.", "approve-error");
    }
  };

  const handleDelete = (id: string) => {
    if (!isAdmin) {
      showErrorToast(
        "Only admins can delete messages.",
        "delete-permission-error",
      );
      return;
    }
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({ type: "delete", id }));
      showSuccessToast("Message deleted successfully!");
    } else {
      console.error("WebSocket is not open. Delete not sent.");
      showErrorToast("Failed to delete message.", "delete-error");
    }
  };

  const fetchProgram = useCallback(async () => {
    if (!currentStream?.id) return;

    try {
      const res = await apiService.get<
        PaginatedResponse<StreamProgramResponse>
      >(`/api/streams/stream-program/?stream=${currentStream?.id}`);

      const program = res.data.results?.[0];

      setStreamProgram(program?.file ?? null);
    } catch (err) {
      console.error("Failed to fetch program", err);
    }
  }, [currentStream?.id]);

  useEffect(() => {
    fetchProgram();
  }, [fetchProgram]);

  const handleDownloadProgram = async () => {
    if (!streamProgram) {
      showErrorToast("No program available to download");
      return;
    }

    try {
      setDownloadingProgram(true);

      const response = await fetch(streamProgram, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to download file");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const contentDisposition = response.headers.get("Content-Disposition");

      let filename = "stream-program";

      if (contentDisposition && contentDisposition.includes("filename=")) {
        filename = contentDisposition.split("filename=")[1].replace(/"/g, "");
      } else {
        filename = decodeURIComponent(
          streamProgram.split("/").pop() || "stream-program",
        );
      }

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      showErrorToast("Failed to download program");
    } finally {
      setDownloadingProgram(false);
    }
  };

  if (isLoadingStreams) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Typography>Loading streams...</Typography>
      </Box>
    );
  }

  if (!streams.length) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Typography>No streams found for this booking.</Typography>
      </Box>
    );
  }

  const isStaging = BaseURL === "https://staging.muzukuru.com";

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: memorialTheme.surface }}>
      {currentStream.booking.service === "Funeral" ||
      currentStream.booking.service === "Funeral_Home" ? (
        <ThemedHeader stream={currentStream} theme={memorialTheme} />
      ) : (
        <EventHeader stream={currentStream} theme={memorialTheme} />
      )}

      <Container
        maxWidth={isMobile ? "sm" : "lg"}
        sx={{ py: { xs: 2, md: 4 } }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {currentStream.booking.service === "Church" ||
          currentStream.booking.service === "Conference" ? (
            <>
              {isMobile ? (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <StreamViewer
                      streams={streams}
                      activeStream={activeStreamIndex}
                      onStreamChange={handleStreamChange}
                      memorialTheme={memorialTheme}
                      currentStream={currentStream}
                      deceasedName={deceasedData?.first_name}
                      hideTabs
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Stack
                      direction="row"
                      spacing={2}
                      justifyContent="space-between"
                      mb={1}
                    >
                      {/* Download Button */}
                      <Button
                        variant="contained"
                        sx={{
                          borderRadius: "50px",

                          textTransform: "none",
                        }}
                        onClick={handleDownloadProgram}
                        disabled={!streamProgram}
                      >
                        {downloadingProgram ? (
                          <CircularProgress size={24} sx={{ color: "#fff" }} />
                        ) : (
                          <>
                            <Typography
                              sx={{ color: "#fff", fontWeight: "bold" }}
                            >
                              Download Program
                            </Typography>
                          </>
                        )}
                      </Button>

                      {/* Share Button */}
                      <IconButton
                        onClick={() => setShareDrawerOpen(true)}
                        sx={{
                          bgcolor: "info.main",
                          color: "#fff",
                          width: 40,
                          height: 40,
                          "&:hover": { bgcolor: "info.light" },
                        }}
                      >
                        <Share />
                      </IconButton>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <ServiceDetails
                      stream={currentStream}
                      theme={memorialTheme}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <ConferenceSidePanel
                        streams={streams}
                        activeStream={activeStreamIndex}
                        onStreamChange={handleStreamChange}
                        memorialTheme={memorialTheme}
                        messages={messages}
                        isAdmin={isAdmin}
                        socketRef={socketRef}
                        isMobile={isMobile}
                        handleApprove={handleApprove}
                        handleDelete={handleDelete}
                        //CommentCard={CommentCard}
                        colors={colors}
                      />
                  </Grid>
                </Grid>
              ) : (
                <>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 8 }}>
                      <StreamViewer
                        streams={streams}
                        activeStream={activeStreamIndex}
                        onStreamChange={handleStreamChange}
                        memorialTheme={memorialTheme}
                        currentStream={currentStream}
                        deceasedName={deceasedData?.first_name}
                        hideTabs
                      />
                      <Box sx={{ mt: 2 }}>
                        <ShareControls
                          streamData={currentStream}
                          theme={memorialTheme}
                          donationsEnabled={
                            currentStream?.go_fund_me_enabled || false
                          }
                          streamProgram={streamProgram}
                          handleDownloadProgram={handleDownloadProgram}
                          downloadingProgram={downloadingProgram}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <ConferenceSidePanel
                        streams={streams}
                        activeStream={activeStreamIndex}
                        onStreamChange={handleStreamChange}
                        memorialTheme={memorialTheme}
                        messages={messages}
                        isAdmin={isAdmin}
                        socketRef={socketRef}
                        isMobile={isMobile}
                        handleApprove={handleApprove}
                        handleDelete={handleDelete}
                        //CommentCard={CommentCard}
                        colors={colors}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid size={{ xs: 12 }}>
                      <ServiceDetails
                        stream={currentStream}
                        theme={memorialTheme}
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </>
          ) : (
            <Grid container spacing={2}>
              {currentStream?.booking.service === "Funeral" ||
              currentStream?.booking.service === "Funeral_Home" ? (
                <>
                  <Grid
                    size={{
                      xs: 12,
                      md: currentStream?.go_fund_me_link ? 8 : 12,
                    }}
                  >
                    <StreamViewer
                      streams={streams}
                      activeStream={activeStreamIndex}
                      onStreamChange={handleStreamChange}
                      memorialTheme={memorialTheme}
                      currentStream={currentStream}
                      deceasedName={deceasedData?.first_name}
                    />
                    {!isMobile && (
                      <Box>
                        <ShareControls
                          streamData={currentStream}
                          theme={memorialTheme}
                          donationsEnabled={
                            currentStream?.go_fund_me_enabled || false
                          }
                          streamProgram={streamProgram}
                          handleDownloadProgram={handleDownloadProgram}
                          downloadingProgram={downloadingProgram}
                        />
                      </Box>
                    )}
                  </Grid>

                  {currentStream?.go_fund_me_enabled &&
                    currentStream?.go_fund_me_link &&
                    !isMobile && (
                      <Grid size={{ xs: 12, md: 4 }}>
                        <GoFundMeWidget url={currentStream.go_fund_me_link} />
                      </Grid>
                    )}
                </>
              ) : null}
            </Grid>
          )}

          {isMobile &&
            currentStream.booking.service !== "Church" &&
            currentStream.booking.service !== "Conference" && (
              <motion.div variants={itemVariants}>
                <Box
                  sx={{
                    position: "sticky",
                    bottom: 0,
                    zIndex: 1000,
                    mb: 2,
                    borderRadius: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleDownloadProgram}
                    sx={{ borderRadius: "20px" }}
                    disabled={!streamProgram}
                  >
                    {downloadingProgram ? (
                      <CircularProgress size={24} sx={{ color: "#fff" }} />
                    ) : (
                      <>
                        <Typography sx={{ color: "#fff", fontWeight: "bold" }}>
                          Program
                        </Typography>
                      </>
                    )}
                  </Button>

                  <IconButton
                    onClick={() => setShareDrawerOpen(true)}
                    sx={{
                      color: "#ffffff",
                      backgroundColor: memorialTheme.primary,
                    }}
                  >
                    <Share />
                  </IconButton>
                </Box>
              </motion.div>
            )}
          {!isMobile &&
            currentStream?.service?.type_of_service !== "Church" && (
              <motion.div variants={itemVariants}></motion.div>
            )}
          <ShareDrawer
            open={shareDrawerOpen}
            onClose={() => setShareDrawerOpen(false)}
            streamData={currentStream}
          />
          <motion.div variants={itemVariants}>
            <Box sx={{ boxShadow: 0, width: "100%" }}>
              {currentStream?.service?.type_of_service === "Church" ? null : (
                <>
                  {currentStream?.booking.service === "Funeral" ||
                  currentStream?.booking.service === "Funeral_Home" ? (
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      {/* Tabs Section */}
                      <Paper
                        elevation={0}
                        sx={{
                          borderRadius: "50px",
                          backgroundColor: "#FFFFFF",
                          width: "100%",
                          px: 2,
                          py: 1,
                          mt: 2,
                        }}
                      >
                        <Tabs
                          value={tabValue}
                          onChange={handleTabChange}
                          variant={isMobile ? "scrollable" : "fullWidth"}
                          scrollButtons="auto"
                          allowScrollButtonsMobile
                          sx={{
                            "& .MuiTabs-flexContainer": {
                              width: "100%",
                              justifyContent: "space-between",
                            },
                            "& .MuiTab-root": {
                              flex: 1,
                              textTransform: "none",
                              fontSize: "1rem",
                              fontWeight: 400,
                              py: 1.5,
                              px: 2,
                              minHeight: "auto",
                              "&:hover": { color: "#000" },
                            },
                            "& .Mui-selected": {
                              color: `${colors.primary[900]} !important`,
                              backgroundColor: reduxTheme.primary_colors[400],
                              borderRadius: "50px",
                              "&:hover": {
                                backgroundColor: memorialTheme.background,
                              },
                            },
                            "& .MuiTabs-indicator": { display: "none" },
                          }}
                        >
                          <Tab label="Obituary" />
                          <Tab label="Memories" />
                          <Tab label="Donations" />
                          <Tab label="Favourites" disabled />
                          <Tab label="Timeline" disabled />
                          <Tab label="Gallery" disabled />
                          <Tab label={isStaging ? "Admin" : "Downloads"} />
                        </Tabs>
                      </Paper>

                      {/* Tab Panels */}
                      <Box sx={{ width: "100%", p: 0, m: 0 }}>
                        <TabPanel
                          value={tabValue}
                          index={0}
                          sx={{ p: 0, m: 0 }}
                        >
                          <ObituaryTab
                            deceasedData={deceasedData}
                            streamData={currentStream}
                            isLoadingDeceased={isLoadingDeceased}
                            isAdmin={isAdmin}
                          />
                        </TabPanel>
                        <TabPanel
                          value={tabValue}
                          index={1}
                          sx={{ p: 0, m: 0 }}
                        >
                          <MemoriesTab
                            messages={messages}
                            isAdmin={isAdmin}
                            socketRef={socketRef}
                            isMobile={isMobile}
                            handleApprove={handleApprove}
                            handleDelete={handleDelete}
                            CommentCard={CommentCard}
                            colors={colors}
                          />
                        </TabPanel>
                        <TabPanel
                          value={tabValue}
                          index={2}
                          sx={{ p: 0, m: 0 }}
                        >
                          <DonationsTab
                            streamData={currentStream}
                            streamId={currentStream?.id?.toString() || ""}
                            isAdmin={isAdmin}
                            isMobile={isMobile}
                            fetchStreamDetails={fetchStreamDetails}
                          />
                        </TabPanel>

                        <TabPanel
                          value={tabValue}
                          index={6}
                          sx={{ p: 0, m: 0 }}
                        >
                          {isStaging ? (
                            <AdminTab />
                          ) : (
                            <DownloadsTab
                              streamId={currentStream?.id?.toString() || ""}
                              vimeoLink={currentStream?.vimeo_stream_link || ""}
                              vimeoPassword={
                                currentStream?.vimeo_download_password || ""
                              }
                              currentStream={currentStream}
                            />
                          )}
                        </TabPanel>
                      </Box>
                    </Box>
                  ) : null}
                </>
              )}
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
}
